import { v4 as uuidv4 } from "uuid";
import {
  collection,
  doc,
  getDocs,
  addDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
  query,
  orderBy,
  limit,
  where,
} from "firebase/firestore";
import { IProjectService, IProjectState } from "../ProjectService";
import { firestoreDB } from "./config";

export default class FireServiceAdapter implements IProjectService {
  async saveProject(params: any): Promise<{ id: string }> {
    const projectId = uuidv4();
    const ts = serverTimestamp();

    const projectInfo = {
      ID: projectId,
      createdAt: ts,
      opportunityName: params.projectBasicInfo.name,
      opportunityAddress: params.projectBasicInfo.address,
      city: params.projectBasicInfo.city,
      state: params.projectBasicInfo.state,
      zip: params.projectBasicInfo.zip,
      bAcquisition: params.projectBasicInfo.budgetAcquisition,
      bConstruction: params.projectBasicInfo.budgetConstruction,
      bSoft: params.projectBasicInfo.budgetSoft,
      bFinance: params.projectBasicInfo.budgetFinance,
      bDev: params.projectBasicInfo.budgetDevelopment,
      cType: params.projectBasicInfo.constructionType,
      pType: params.projectBasicInfo.projectType,
      mr_mbe: params.projectMs.minorityBusiness,
      mr_wbe: params.projectMs.womanOwned,
      mr_sbe: params.projectMs.smallBusiness,
      localHiring: params.projectMs.localHiring,
    };

    const projectTeam = {
      ID: uuidv4(),
      createdAt: ts,
      Project_ID: projectId,
      developer: params.projectTeam.developer,
      tech_assist: params.projectTeam.technicalAssistance,
      CCLF_rep: params.projectTeam.cclfRepresentative,
      CHA_rep: params.projectTeam.cdhRepresentative,
      architect: params.projectTeam.architect,
      engineer_civil: params.projectTeam.civilEngineer,
      engineer_structural: params.projectTeam.structuralEngineer,
      general_contractor: params.projectTeam.generalContractor,
    };

    const projectSchedule = {
      ID: uuidv4(),
      createdAt: ts,
      Project_ID: projectId,
      schedAcq_PSA: params.acquisitionSchedule.contractExecution,
      schedAcq_DD: params.acquisitionSchedule.dueDiligenceEnds,
      schedAcq_Settle: params.acquisitionSchedule.settlement,
      schedFin_Term: params.capitalizationSchedule.termSheetsReceived,
      schedFin_Commit: params.capitalizationSchedule.commitmentLetterExecuted,
      schedFin_Close: params.capitalizationSchedule.financialClose,
      schedProcure_Request: params.procurementSchedule.releaseRequests,
      schedProcure_Proposals: params.procurementSchedule.receiveProposals,
      schedProcure_Awards: params.procurementSchedule.makeAwards,
      schedProcure_Negotiate: params.procurementSchedule.negotiateContracts,
      schedProcure_Execute: params.procurementSchedule.executeContracts,
      schedDev_Bplan: params.developmentSchedule.businessPlanFinalized,
      schedDev_GMPAg: params.developmentSchedule.constructionContractExecuted,
      schedDev_PropManAg: params.developmentSchedule.listingAgentAgreement,
      schedDes_KickOff: params.designSchedule.sowFinalized,
      schedDes_Schematic: params.designSchedule.schematicSet,
      schedDes_Development: params.designSchedule.developmentDocumentSet,
      schedDes_Const: params.designSchedule.constructionDocumentSet,
      schedDes_PermObt: params.designSchedule.permitsObtained,
      schedCon_KickOff: params.constructionSchedule.kickoffMeeting,
      schedCon_Mob: params.constructionSchedule.mobilization,
      schedCon_50: params.constructionSchedule.fiftyCompletion,
      schedCon_75: params.constructionSchedule.seventyFiveCompletion,
      schedCon_Comp: params.constructionSchedule.constructionCompletion,
    };

    // await this.collectionPostRequest("project_info", projectinfo);
    // await this.collectionPostRequest("project_team", projectTeam);
    // await this.collectionPostRequest("project_sched", projectSchedule);

    const projectInfoColRef = collection(firestoreDB, "project_info");
    const projectTeamColRef = collection(firestoreDB, "project_team");
    const projectScheduleColRef = collection(firestoreDB, "project_sched");
    await addDoc(projectInfoColRef, projectInfo);
    await addDoc(projectTeamColRef, projectTeam);
    await addDoc(projectScheduleColRef, projectSchedule);

    return {
      id: projectInfo.ID,
    };
  }

  async fetchProject(id: string): Promise<IProjectState> {
    let projectState = {} as IProjectState;
    const projectInfo = await this.collectionFindByIDRequest(
      "project_info",
      id
    );

    projectState.projectBasicInfo = {
      ID: projectInfo.ID,
      name: projectInfo.projectName,
      address: projectInfo.projectAddress
        ? projectInfo.projectAddress
        : "unknown street",
      city: projectInfo.city ? projectInfo.city : "Chicago",
      state: projectInfo.state ? projectInfo.state : "IL",
      zip: projectInfo.zip ? projectInfo.zip : "12345",
      projectType: projectInfo.pType,
      constructionType: projectInfo.cType,
      budgetAcquisition: projectInfo.bAcquisition,
      budgetConstruction: projectInfo.bConstruction,
      budgetSoft: projectInfo.bSoft,
      budgetFinance: projectInfo.bFinance,
      budgetDevelopment: projectInfo.bDev,
      projectBudget: projectInfo.projectBudget ? projectInfo.projectBudget : 0,
    };

    projectState.projectMs = {
      minorityBusiness: projectInfo.mr_mbe,
      womanOwned: projectInfo.mr_wbe,
      smallBusiness: projectInfo.mr_sbe,
      localHiring: projectInfo.localHiring ? projectInfo.localHiring : 0,
      // davisBacon: projectInfo.mr_davisBacon,
      // minoritySetAside: projectInfo.mr_minorityAside
    };

    const projectTeam = await this.collectionFindByProjectIDRequest(
      "project_team",
      id
    );

    projectState.projectTeam = {
      developer: projectTeam.developer,
      technicalAssistance: projectTeam.tech_assist,
      cclfRepresentative: projectTeam.CCLF_rep,
      cdhRepresentative: projectTeam.CHA_rep,
      architect: projectTeam.architect,
      civilEngineer: projectTeam.engineer_civil,
      structuralEngineer: projectTeam.engineer_structural,
      generalContractor: projectTeam.general_contractor,
    };

    const projectSchedule = await this.collectionFindByProjectIDRequest(
      "project_sched",
      id
    );

    projectState.acquisitionSchedule = {
      contractExecution: projectSchedule.schedAcq_PSA,
      dueDiligenceEnds: projectSchedule.schedAcq_DD,
      settlement: projectSchedule.schedAcq_Settle,
    };
    projectState.capitalizationSchedule = {
      termSheetsReceived: projectSchedule.schedFin_Term,
      commitmentLetterExecuted: projectSchedule.schedFin_Commit,
      financialClose: projectSchedule.schedFin_Close,
    };
    projectState.constructionSchedule = {
      kickoffMeeting: projectSchedule.schedCon_KickOff,
      mobilization: projectSchedule.schedCon_Mob,
      fiftyCompletion: projectSchedule.schedCon_50,
      seventyFiveCompletion: projectSchedule.schedCon_75,
      constructionCompletion: projectSchedule.schedCon_Comp,
    };
    projectState.designSchedule = {
      sowFinalized: projectSchedule.schedDes_KickOff,
      schematicSet: projectSchedule.schedDes_Schematic,
      developmentDocumentSet: projectSchedule.schedDes_Development,
      constructionDocumentSet: projectSchedule.schedDes_Const,
      permitsObtained: projectSchedule.schedDes_PermObt,
    };
    projectState.developmentSchedule = {
      businessPlanFinalized: projectSchedule.schedDev_Bplan,
      listingAgentAgreement: projectSchedule.schedDev_PropManAg,
      constructionContractExecuted: projectSchedule.schedDev_GMPAg,
    };
    projectState.procurementSchedule = {
      releaseRequests: projectSchedule.schedProcure_Request,
      receiveProposals: projectSchedule.schedProcure_Proposals,
      makeAwards: projectSchedule.schedProcure_Awards,
      negotiateContracts: projectSchedule.schedProcure_Negotiate,
      executeContracts: projectSchedule.schedProcure_Execute,
    };

    return projectState;
  }

  async listProjects(): Promise<IProjectState[]> {
    const projects = await this.collectionAll("project_info");
    return projects.map(
      (p: any) =>
        ({
          projectBasicInfo: {
            ID: p.ID,
            name: p.projectName,
            address: p.projectAddress ? p.projectAddress : "unknown street",
            city: p.city ? p.city : "Chicago",
            state: p.state ? p.state : "IL",
            zip: p.zip ? p.zip : "12345",
            constructionType: p.cType,
            projectType: p.pType,
          },
        } as IProjectState)
    );
  }

  async deleteProject(id: string): Promise<void> {
    await this.documentDeleteRequest("project_info", id);
    await this.documentDeleteRequestByProjectID("project_team", id);
    await this.documentDeleteRequestByProjectID("project_sched", id);
  }

  async documentDeleteRequest(col: string, id: string) {
    const docRef = doc(firestoreDB, col, id);
    await deleteDoc(docRef);

    // const ref = await firestoreDB.collection(collection).where("ID", "==", id);
    // ref.get().then(function (querySnapshot) {
    //   querySnapshot.forEach(function (doc) {
    //     doc.ref.delete();
    //   });
    // });
  }

  async documentDeleteRequestByProjectID(col: string, id: string) {
    const docRef = doc(firestoreDB, col, id);
    await deleteDoc(docRef);

    // const ref = await fireStoreDB
    //   .collection(collection)
    //   .where("Project_ID", "==", id);
    // ref.get().then(function (querySnapshot) {
    //   querySnapshot.forEach(function (doc) {
    //     doc.ref.delete();
    //   });
    // });
  }

  // async collectionPostRequest(collection: string, items: {}) {
  //   await fireStoreDB.collection(collection).add(items);
  // }

  async collectionFindByIDRequest(col: string, id: string): Promise<any> {
    const docRef = doc(firestoreDB, col, id);
    await onSnapshot(docRef, (doc) => {
      return doc.data;
    });
    // const ref = await fireStoreDB
    //   .collection(collection)
    //   .where("ID", "==", id)
    //   .get();
    // const docs = ref.docs.map((doc) => doc.data());
    // return docs[0];
  }

  async collectionFindByProjectIDRequest(
    col: string,
    id: string
  ): Promise<any> {
    const docRef = doc(firestoreDB, col, id);
    await onSnapshot(docRef, (doc) => {
      return doc.data;
    });
    // const ref = await fireStoreDB
    //   .collection(collection)
    //   .where("Project_ID", "==", id)
    //   .get();
    // const docs = ref.docs.map((doc) => doc.data());
    // return docs[0];
  }

  async collectionAll(col: string): Promise<any> {
    const colRef = collection(firestoreDB, col);
    const q = query(colRef, orderBy("createdAt"), limit(8));
    await onSnapshot(q, (snapshot) => {
      return snapshot.docs.forEach((doc) => doc.data);
    });
    // const ref = await fireStoreDB
    //   .collection(collection)
    //   .orderBy("createdAt", "desc")
    //   .limit(8)
    //   .get();
    // return ref.docs.map((doc) => doc.data());
  }

  async fetchTask(col: string, phase: string, projectID: string) {
    const colRef = collection(firestoreDB, col);
    const q = query(
      colRef,
      where("projectID", "==", projectID),
      where("phase", "==", phase)
    );
    await onSnapshot(q, (snapshot) => {
      return snapshot;
    });

    // const ref = await fireStoreDB
    //   .collection(collection)
    //   .where("phase", "==", phase)
    //   .where("projectID", "==", projectID)
    //   .get();
    // return ref;
  }
  async fetchStaticTask(col: string, phase: string) {
    const colRef = collection(firestoreDB, col);
    const q = query(colRef, where("phase", "==", phase));
    await onSnapshot(q, (snapshot) => {
      return snapshot;
    });
    // const ref = await fireStoreDB
    //   .collection(collection)
    //   .where("phase", "==", phase)
    //   .get();
    // return ref;
  }
}
